import { track } from '@eventbrite/datalayer-library';
import { majorToMinor } from '@eventbrite/intl';
import { Environment } from '@eventbrite/paypal-checkout-types';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { trackPaypalCTAClick } from '../../../../actions/eventAnalytics';
import {
    onPaypalError,
    onPaypalPaymentAuthorizeHandler,
} from '../../../../actions/paypal';
import { logPaypalPlaceOrderClickInOrderForm } from '../../../../actions/statsig';
import { PaypalBuyerDetails } from '../../../../models/paymentMethods/paypal';
import { getCurrencyFormat, getIsProd } from '../../../../selectors/app';
import { getCurrency } from '../../../../selectors/event';
import {
    getCurrencyFromOrder,
    getOrderCosts,
    getTotalAmount,
    getUnformattedOrderCost,
} from '../../../../selectors/order';
import { getPayInAmount } from '../../../../selectors/payment';
import { getPaypalScriptStatus } from '../../../../selectors/paypal';
import { getRoutePathname, isPayInPage } from '../../../../selectors/routing';
import {
    getBreakdownItems,
    getSelectedTickets,
} from '../../../../selectors/tickets';
import { getSelectedVirtualIncentives } from '../../../../selectors/virtualIncentive';
import { CheckoutState } from '../../../../state';
import { EventNamespace } from '../../../../state/event';
import { OrderNamespace } from '../../../../state/order';
import { getTotalDiscountingVirtualIncentives } from '../../../../utils/virtualIncentives';
import {
    PaypalButtonCallbackProps,
    PaypalButtonNonCallbackProps,
} from '../PaypalButton/PaypalButton';

const getAmountTotal = (state: CheckoutState) => {
    if (isPayInPage(state)) {
        return getPayInAmount(state);
    }

    const costs = getOrderCosts(state, false);

    return costs
        ? majorToMinor(
              getTotalDiscountingVirtualIncentives(
                  getUnformattedOrderCost(state),
                  getSelectedVirtualIncentives(state),
              ),
              getCurrencyFromOrder(state),
          )
        : getTotalAmount(state);
};

const mapStateToProps = (
    state: CheckoutState & {
        order: OrderNamespace;
        event: EventNamespace;
    },
): Pick<
    PaypalButtonNonCallbackProps,
    | 'amountCurrency'
    | 'amountTotal'
    | 'breakdownItems'
    | 'currencyFormat'
    | 'env'
    | 'isCheckoutButton'
    | 'paypalScriptStatus'
    | 'selectedTickets'
> => ({
    amountCurrency: getCurrency(state) || 'USD',
    amountTotal: getAmountTotal(state) || 0,
    breakdownItems: getBreakdownItems(state),
    currencyFormat: getCurrencyFormat(state) || '',
    env: getIsProd(state) ? Environment.Production : Environment.Sandbox,
    isCheckoutButton: true,
    paypalScriptStatus: getPaypalScriptStatus(state),
    selectedTickets: getSelectedTickets(state),
});

const mapDispatchToProps = (
    dispatch: Dispatch<CheckoutState>,
): PaypalButtonCallbackProps => ({
    onPaypalAuthorize: (buyerDetails: PaypalBuyerDetails) => {
        dispatch(onPaypalPaymentAuthorizeHandler(buyerDetails));
    },
    onPaypalClick: ({ fundingSource }) => {
        dispatch(trackPaypalCTAClick(fundingSource));
        dispatch(logPaypalPlaceOrderClickInOrderForm);

        track({
            eventName: 'Paypal Button - Click',
            eventData: {
                funding: fundingSource,
            },
        });
    },
    onPaypalError: (error: Error) => {
        dispatch(onPaypalError(error));
    },
    onPaypalInit: () => {
        dispatch((_dispatch, getState) => {
            track({
                eventName: 'Paypal Button - View',
                eventData: {
                    pathname: getRoutePathname(getState()),
                },
            });
        });
    },
});

export const connectPayPalButton = connect(mapStateToProps, mapDispatchToProps);
